import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { Icon } from 'semantic-ui-react';
import classNames from 'classnames';
import { OrganizationSwitcher } from '@clerk/clerk-react';
import { usePopup } from '../../lib/popup';

import Paths from '../../constants/Paths';
import NotificationsStep from '../Header/NotificationsStep';
import User from '../User';
import UserStep from '../UserStep';
import BoardActionsContainer from '../../containers/BoardActionsContainer';

import styles from './MobileDrawer.module.scss';

const POPUP_PROPS = {
  position: 'bottom left',
};

const MobileDrawer = React.memo(
  ({
    isOpen,
    onClose,
    user,
    project,
    notifications,
    boards,
    currentBoardId,
    isLogouting,
    canEditProject,
    onProjectSettingsClick,
    onNotificationDelete,
    onUserSettingsClick,
    onLogout,
  }) => {
    const overlayRef = useRef(null);
    const location = useLocation();

    const NotificationsPopup = usePopup(NotificationsStep, POPUP_PROPS);
    const UserPopup = usePopup(UserStep, POPUP_PROPS);

    const handleOverlayClick = useCallback(
      e => {
        if (e.target === overlayRef.current) {
          onClose();
        }
      },
      [onClose]
    );

    useEffect(() => {
      if (isOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
      return () => {
        document.body.style.overflow = '';
      };
    }, [isOpen]);

    useEffect(() => {
      onClose();
    }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <div
        ref={overlayRef}
        className={classNames(styles.overlay, isOpen && styles.overlayOpen)}
        onClick={handleOverlayClick}
        role="presentation"
      >
        <div className={classNames(styles.drawer, isOpen && styles.drawerOpen)}>
          {/* User section */}
          <div className={styles.userSection}>
            <User name={user.name} avatarUrl={user.avatarUrl} size="large" />
            <div className={styles.userInfo}>
              <div className={styles.userName}>{user.name}</div>
              <div className={styles.userEmail}>{user.email || ''}</div>
            </div>
          </div>

          {/* Organization Switcher */}
          <div className={styles.section}>
            <OrganizationSwitcher
              hidePersonal={false}
              appearance={{
                elements: {
                  rootBox: {
                    width: '100%',
                  },
                  organizationSwitcherTrigger: {
                    width: '100%',
                    justifyContent: 'flex-start',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.04)',
                    },
                  },
                },
              }}
            />
          </div>

          <div className={styles.divider} />

          {/* Navigation */}
          <div className={styles.section}>
            <Link to={Paths.ROOT} className={styles.navItem} onClick={onClose}>
              <Icon name="th" className={styles.navIcon} />
              <span>Projects</span>
            </Link>
            <NotificationsPopup items={notifications} onDelete={onNotificationDelete}>
              <button type="button" className={styles.navItem}>
                <Icon name="bell" className={styles.navIcon} />
                <span>Notifications</span>
                {notifications.length > 0 && (
                  <span className={styles.badge}>{notifications.length}</span>
                )}
              </button>
            </NotificationsPopup>
          </div>

          {/* Boards list (when in a project) */}
          {project && boards && boards.length > 0 && (
            <>
              <div className={styles.divider} />
              <div className={styles.section}>
                <div className={styles.sectionTitle}>
                  <Icon name="clipboard outline" />
                  Boards
                </div>
                <div className={styles.boardsList}>
                  {boards.map(board => (
                    <Link
                      key={board.id}
                      to={Paths.BOARDS.replace(':id', board.id)}
                      className={classNames(
                        styles.boardItem,
                        board.id === currentBoardId && styles.boardItemActive
                      )}
                      onClick={onClose}
                    >
                      {board.name}
                    </Link>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Show board members in drawer */}
          {currentBoardId && (
            <>
              <div className={styles.divider} />
              <div className={styles.section}>
                <BoardActionsContainer isMobileVisible showOnlyMemberships />
              </div>
            </>
          )}

          {/* Bottom actions */}
          <div className={styles.bottomSection}>
            <div className={styles.divider} />
            {canEditProject && project && (
              <button
                type="button"
                className={styles.navItem}
                onClick={() => {
                  onProjectSettingsClick();
                  onClose();
                }}
              >
                <Icon name="setting" className={styles.navIcon} />
                <span>Project Settings</span>
              </button>
            )}
            <UserPopup
              isLogouting={isLogouting}
              onSettingsClick={onUserSettingsClick}
              onLogout={onLogout}
            >
              <button type="button" className={styles.navItem}>
                <Icon name="user" className={styles.navIcon} />
                <span>Account Settings</span>
              </button>
            </UserPopup>
          </div>
        </div>
      </div>
    );
  }
);

MobileDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  project: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  notifications: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  boards: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  currentBoardId: PropTypes.string,
  isLogouting: PropTypes.bool.isRequired,
  canEditProject: PropTypes.bool.isRequired,
  onProjectSettingsClick: PropTypes.func.isRequired,
  onNotificationDelete: PropTypes.func.isRequired,
  onUserSettingsClick: PropTypes.func.isRequired,
  onLogout: PropTypes.func.isRequired,
};

MobileDrawer.defaultProps = {
  project: undefined,
  boards: undefined,
  currentBoardId: undefined,
};

export default MobileDrawer;
